import html from 'virtual:app-ui-html';
import mainUi from 'virtual:main-ui-entry';
import type { RuntimeConfig } from '@borradh-workspace/runtime-config/schema';
import { Capacitor } from '@capacitor/core';

import { rehydrateAuthToken } from '@/lib/auth-token';
import { getRequestAuthToken } from '@/lib/request-auth-token';
import { getRuntimeConfigFetchUrl } from '@/lib/runtime-config-url';
import {
  applyAppUiHtml,
  captureAppUiHtml,
  createAppUiReloadGuard,
  loadAppUiStyles,
} from './document';
import { mountRecoveryPage } from './recovery-page';
import { handOffRuntimeConfig } from './runtime-config-handoff';
import { connectAppUiRefresh, resolveAppUi, startAppUi } from './selection';
import type { AppUiVersion } from './types';

/**
 * Same validation as before, but the schema (and zod) load beside the request
 * instead of inside the shell entry, which must stay small to run quickly.
 */
async function loadRuntimeConfig(): Promise<RuntimeConfig | null> {
  if (Capacitor.isNativePlatform()) {
    const { getBuildTimeRuntimeConfig } = await import(
      '@/lib/build-time-runtime-config'
    );
    const buildTime = getBuildTimeRuntimeConfig();
    if (buildTime) return buildTime;
  }
  const schema = import('@borradh-workspace/runtime-config/schema');
  schema.catch(() => undefined);
  const url = getRuntimeConfigFetchUrl();
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 3_000);
  try {
    const response = await fetch(url, {
      credentials: 'include',
      cache: 'no-store',
      signal: controller.signal,
    });
    if (!response.ok) return null;
    const body = await response.text();
    const parsed = (await schema).runtimeConfigSchema.safeParse(
      JSON.parse(body)
    );
    if (!parsed.success) return null;
    // The chosen UI asks for this same document as it first renders.
    handOffRuntimeConfig({ url, body });
    return parsed.data;
  } catch {
    // The chosen main app retains its own runtime-config recovery path.
    return null;
  } finally {
    clearTimeout(timer);
  }
}

/** No router, app component or app stylesheet may be imported above this gate. */
async function boot() {
  const rehydrated = rehydrateAuthToken().catch(() => undefined);
  const config = await loadRuntimeConfig();
  await rehydrated;

  // Dev only: force a UI locally with `VITE_DEV_FEATURE_FLAGS`
  // (`app-ui-dev-main` → main, `rollout-app-v2` → V2). The contracts barrel is
  // large and must stay out of the shell, so it loads only in dev.
  let localUi: AppUiVersion | undefined;
  if (import.meta.env.DEV) {
    const flags = String(import.meta.env.VITE_DEV_FEATURE_FLAGS ?? '')
      .split(',')
      .map((flag) => flag.trim());
    const { APP_UI_DEV_MAIN_KEY, APP_V2_ROLLOUT_FLAG } = await import(
      '@borradh-workspace/contracts'
    );
    if (flags.includes(APP_UI_DEV_MAIN_KEY)) localUi = 'main';
    else if (flags.includes(APP_V2_ROLLOUT_FLAG)) localUi = 'v2';
  }
  const apiUrl = config?.apiUrl;
  // V2 is the default UI: without an API to ask, or when asking fails, boot V2.
  const decide = (token?: string): Promise<AppUiVersion> =>
    localUi
      ? Promise.resolve(localUi)
      : apiUrl
        ? resolveAppUi({ apiUrl, token })
        : Promise.resolve('v2');
  const version = await decide(getRequestAuthToken());
  let disconnect: (() => void) | undefined;
  window.__BORRADH_APP_UI__ = {
    version,
    connect(connection) {
      disconnect?.();
      const guard = createAppUiReloadGuard();
      const stop = connectAppUiRefresh({
        version,
        connection,
        decide,
        currentUrl: () => window.location.href,
        reload: () => window.location.reload(),
        activityRevision: guard.activityRevision,
        isBlocked: guard.isBlocked,
      });
      disconnect = () => {
        stop();
        guard.dispose();
      };
    },
  };
  document.documentElement.dataset.appUi = version;
  const mainShell = captureAppUiHtml();
  let v2Applied = false;
  // `version` above stays the API's decision, so the refresh check does not
  // reload a main fallback straight back into a V2 bundle that cannot load.
  await startAppUi({
    version,
    startV2: async () => {
      v2Applied = true;
      applyAppUiHtml(html.v2);
      await import('../main');
    },
    startMain: async () => {
      if (v2Applied) {
        // The V2 bundle failed to load: put main's own shell back first.
        applyAppUiHtml(mainShell);
        document.documentElement.dataset.appUi = 'main';
      }
      // A separate build keeps main's routes, globals and CSS out of the V2
      // graph. This URL is emitted by our build, never supplied by a request.
      await loadAppUiStyles(mainUi.styles);
      await import(/* @vite-ignore */ mainUi.entry);
    },
  });
}

void boot().catch(() => {
  const root = document.getElementById('root');
  if (!root) return;
  mountRecoveryPage(root, () => window.location.reload());
});
